// ANCHOR React
import { useEffect } from 'react';

// ANCHOR Next
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';

// ANCHOR Query String
import { ParsedUrlQuery } from 'querystring';

// ANCHOR API
import { getCoursePageGSSP, getProviderPageGSSP } from '@lyon/utils/api/gssp';
import { getModuleItem } from '@lyon/utils/api/module-item';

// ANCHOR Graph States
import { useAuthAdmin } from '@lyon/nodes/firebase/auth/auth-admin-node';

// ANCHOR Results
import { ICourseResult } from '@lyon/utils/api/results/course';
import { IProviderResult } from '@lyon/utils/api/results/provider';
import { IModuleItemResult } from '@lyon/utils/api/results/module-item';

// ANCHOR Utils
import { ucarecdn } from '@lyon/utils/ucarecdn';
import { getContentPageDescription } from '@lyon/utils/extras/getContentPageDescription';
import { track } from '@lyon/utils/segment';

// ANCHOR Models
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';

// ANCHOR Components
import { LyonAppHead } from '@lyon/components/head/LyonAppHead';
import { FullPageLoader } from '@lyon/components/full-page-loader';
import { Lyon404 } from '@lyon/components/404/Lyon404';
import { LyonBannerToast } from '@lyon/components/banner-toast/LyonBannerToast';

interface IProps {
  id: string;
  data: IModuleItemResult;
  courseData: ICourseResult;
  providerData: IProviderResult;
}

interface IParams extends ParsedUrlQuery {
  pid: string;
  miid: string;
}

export const getServerSideProps: GetServerSideProps<IProps, IParams> = async (context) => {
  if (context.params) {
    const { pid: courseId, miid: moduleItemId } = context.params;

    const data = await getModuleItem({ moduleItemId }, true);
    const courseData = await getCoursePageGSSP(courseId);

    if (data && courseData) {
      const providerData = await getProviderPageGSSP(courseData.providerId);

      if (providerData) {
        return {
          props: {
            id: moduleItemId,
            data,
            courseData,
            providerData,
          },
        };
      }
    }
  }
  return {
    notFound: true,
  };
};

interface IMainProps {
  id: string;
  initialData: IModuleItemResult;
  courseInitialData: ICourseResult;
}

const LyonCourseContent = dynamic<IMainProps>(
  () => (
    import('@lyon/components/course-content/LyonCourseContent')
      .then((mod) => mod.LyonCourseContent)
  ),
  {
    loading: () => <FullPageLoader />,
  },
);

interface IRestoreProps {
  data: ICourseResult;
}

function RestoreCourse({ data }: IRestoreProps) {
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);

  if (typeof isProvider === 'undefined') {
    return <FullPageLoader />;
  }

  return (
    <>
      {isProvider && (
        <LyonBannerToast
          content={`The course "${data.title}" has been deleted and is no longer available.`}
          show={data.isArchived}
          isRestoreCourse
        />
      )}
      <Lyon404 />
    </>
  );
}

// eslint-disable-next-line import/no-default-export
export default function LyonCourseContentPage({
  id, data, courseData, providerData,
}: IProps) {
  const { title, description: contentDescription } = courseData;

  const description = getContentPageDescription({
    title,
    contentDescription,
  });

  const isAdmin = useAuthAdmin();

  useEffect(() => {
    track('Module Item Viewed', {
      name: data.title,
      type: data.itemType,
      isFree: data.isFree,
      parentModule: data.courseModuleId,
      parentCourse: courseData.id,
      itemPosition: data.order,
      url: `https://lyon.com.ph/course/${courseData.id}/content/${id}`,
    }, isAdmin, {
      context: {
        groupId: courseData.providerId,
      },
    });
  }, [
    courseData.providerId, courseData.id, data.courseModuleId, data.id,
    data.isFree, data.itemType, data.order, data.title, id, isAdmin,
  ]);

  if (courseData.isArchived) {
    return (
      <>
        <LyonAppHead
          title="Lyon | Not Found"
          description="Oops! This page doesn't exist."
          openGraph={{
            url: `https://lyon.com.ph/course/${courseData.id}/content/${id}`,
            width: '1366',
            height: '768',
            image: './404.svg',
          }}
        />
        <ProviderCheckManager.Provider providerId={providerData.id}>
          <RestoreCourse data={courseData} />
        </ProviderCheckManager.Provider>
      </>
    );
  }

  return (
    <>
      <LyonAppHead
        title={title}
        description={description}
        openGraph={{
          url: `https://lyon.com.ph/course/${courseData.id}/content/${id}`,
          width: '1366',
          height: '768',
          image: ucarecdn(courseData.courseDisplayPhotoUuid),
        }}
      />
      <LyonCourseContent id={id} initialData={data} courseInitialData={courseData} />
    </>
  );
}
